import React, { useContext, useEffect, useRef } from 'react';

import AppContext from '../../context';
import {
    trackBannerClick,
    trackBannerImpression,
} from '../../tracking/bannerTracking';
import { UiBannerItem } from '../../types';

import styles from './FlyoutBanner.module.scss';

interface FlyoutBannerProps {
    banner: UiBannerItem | null;
    animalType?: string;
    categoryType?: string;
}

export function FlyoutBanner({
    banner,
    animalType,
    categoryType,
}: FlyoutBannerProps): React.JSX.Element | null {
    const { siteId, locale } = useContext(AppContext);

    const trackedImpressionsRef = useRef(new Set<string>());

    useEffect(() => {
        const desktopImage = banner?.desktopImage;

        if (!desktopImage) {
            return;
        }

        const impressionKey = [
            desktopImage,
            categoryType ?? '',
            animalType ?? '',
            siteId ?? '',
            locale ?? '',
        ].join('|');

        if (trackedImpressionsRef.current.has(impressionKey)) {
            return;
        }

        trackedImpressionsRef.current.add(impressionKey);

        trackBannerImpression({ categoryType, animalType, siteId, locale });
    }, [banner?.desktopImage, categoryType, animalType, siteId, locale]);

    if (!banner?.desktopImage) {
        return null;
    }

    const { targetLink, linkAriaLabel, desktopImage, desktopImageAltText } =
        banner;

    const handleClick = () => {
        trackBannerClick({ categoryType, animalType, siteId, locale });
    };

    return (
        <a
            href={targetLink}
            aria-label={linkAriaLabel ?? undefined}
            className={styles.bannerLink}
            data-zta="category-flyout-banner"
            onClick={handleClick}>
            <img
                src={desktopImage}
                alt={desktopImageAltText ?? ''}
                className={styles.bannerImage}
            />
        </a>
    );
}
